import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Play, ExternalLink, Film, Clapperboard, Camera, X, FileDown, Zap } from 'lucide-react';
import { trackPageView } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categoryIcons = {
  'Factory Tour': Camera,
  'Brand Film': Film,
  'Products': Clapperboard,
  'Quality': Clapperboard,
  'Lion Composite Cement': Zap,
};

const MediaPage = () => {
  const [media, setMedia] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const [activeFilter, setActiveFilter] = useState('All');
  const videoRef = useRef(null);

  useEffect(() => {
    trackPageView('/media', 'Media Hub');
    fetch(`${BACKEND_URL}/api/media`)
      .then(res => res.json())
      .then(data => setMedia(data))
      .catch(err => console.error('Error fetching media:', err));
  }, []);

  const categories = media ? ['All', ...new Set(media.videos.map(v => v.category))] : ['All'];
  const filteredVideos = media?.videos?.filter(v => activeFilter === 'All' || v.category === activeFilter) || [];

  const handleClose = () => {
    if (videoRef.current) { videoRef.current.pause(); }
    setActiveVideo(null);
  };

  return (
    <div className="min-h-screen bg-void" data-testid="media-page">
      <SEOHead
        title="Media Hub"
        description="Watch factory tour videos, Lion Cement brand films, and download the Avi Cement product brochure. 12+ videos showcasing our manufacturing excellence."
        path="/media"
      />
      {/* Hero */}
      <section className="relative py-12 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <motion.div
          className="absolute top-1/2 right-0 w-[700px] h-[700px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(232,90,28,0.08) 0%, transparent 60%)' }}
        />
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <span className="text-coral font-mono text-sm mb-4 block">MEDIA HUB</span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Hamari Kahani<br /><span className="text-gradient">Motion Mein</span>
            </h1>
            <p className="text-gray-400 text-lg">Brand films, factory tours, aur behind-the-scenes content — Avi Cement Limited se.</p>
          </motion.div>
        </div>
      </section>

      {/* Video Player Modal */}
      {activeVideo && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={handleClose}>
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="w-full max-w-4xl relative"
            onClick={e => e.stopPropagation()}
          >
            {activeVideo.type === 'mp4' ? (
              <video
                ref={videoRef}
                src={activeVideo.url}
                controls
                autoPlay
                className="w-full rounded-2xl aspect-video bg-black"
                data-testid="video-player-mp4"
              />
            ) : (
              <iframe
                src={`https://www.youtube.com/embed/${activeVideo.id}?autoplay=1`}
                title="Video Player"
                className="w-full rounded-2xl aspect-video"
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
            )}
            <button
              onClick={handleClose}
              className="absolute -top-12 right-0 text-white/70 hover:text-white flex items-center gap-2 font-display text-sm"
              data-testid="close-video-modal"
            >
              Band Karein <X className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      )}

      {/* Filter */}
      <section className="py-6 bg-surface sticky top-20 z-40 border-y border-white/5">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0">
            <Film className="w-5 h-5 text-gray-500 flex-shrink-0" />
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveFilter(cat)}
                data-testid={`media-filter-${cat.toLowerCase().replace(' ', '-')}`}
                className={`px-5 py-2 rounded-full font-display text-sm whitespace-nowrap transition-all ${
                  activeFilter === cat
                    ? 'bg-coral text-white'
                    : 'bg-surface-light text-gray-400 hover:text-white hover:bg-white/10'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Videos Grid */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          {/* Featured Video - First one large */}
          {filteredVideos.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-12"
            >
              <button
                onClick={() => setActiveVideo(filteredVideos[0])}
                data-testid="featured-video"
                className="group w-full relative aspect-video md:aspect-[21/9] rounded-2xl overflow-hidden bg-surface border border-white/5"
              >
                {filteredVideos[0].type === 'mp4' ? (
                  <video
                    src={filteredVideos[0].url}
                    muted
                    className="w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-all duration-500"
                  />
                ) : (
                  <img
                    src={filteredVideos[0].thumbnail}
                    alt={`${filteredVideos[0].title} - Avi Cement video`}
                    loading="lazy"
                    width="800"
                    height="450"
                    className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-500"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-void via-void/30 to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-12">
                  <span className="text-coral font-mono text-xs mb-2">{filteredVideos[0].category}</span>
                  <h2 className="font-display text-2xl md:text-4xl font-bold text-white mb-2">{filteredVideos[0].title}</h2>
                  <p className="text-gray-400 text-sm max-w-xl mb-4">{filteredVideos[0].description}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-full bg-coral flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play className="w-6 h-6 text-white fill-white ml-1" />
                    </div>
                    <span className="text-white font-display text-sm">Abhi Dekhein</span>
                  </div>
                </div>
              </button>
            </motion.div>
          )}

          {/* Rest of videos */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVideos.slice(1).map((video, i) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <button
                  onClick={() => setActiveVideo(video)}
                  data-testid={`video-card-${video.id}`}
                  className="group w-full text-left bg-surface border border-white/5 hover:border-white/20 rounded-2xl overflow-hidden transition-all card-lift"
                >
                  <div className="relative aspect-video">
                    {video.type === 'mp4' ? (
                      <video
                        src={video.url}
                        muted
                        className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity"
                      />
                    ) : (
                      <img
                        src={video.thumbnail}
                        alt={`${video.title} - Avi Cement video thumbnail`}
                        loading="lazy"
                        width="400"
                        height="225"
                        className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-coral/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Play className="w-5 h-5 text-white fill-white ml-0.5" />
                      </div>
                    </div>
                  </div>
                  <div className="p-5">
                    <span className="text-xs font-mono text-gray-500 uppercase">{video.category}</span>
                    <h3 className="font-display text-lg font-semibold text-white mt-1 mb-2 group-hover:text-coral transition-colors">{video.title}</h3>
                    <p className="text-gray-500 text-sm line-clamp-2">{video.description}</p>
                  </div>
                </button>
              </motion.div>
            ))}
          </div>

          {filteredVideos.length === 0 && (
            <div className="text-center py-16">
              <Film className="w-12 h-12 text-gray-700 mx-auto mb-4" />
              <p className="text-gray-500">Is category mein koi video nahi hai.</p>
            </div>
          )}
        </div>
      </section>

      {/* Brochure Download */}
      {media?.brochure_url && (
        <section className="py-16 border-t border-white/5">
          <div className="container mx-auto px-4 md:px-8 max-w-7xl">
            <div className="bg-gradient-to-r from-[#E85A1C]/10 to-[#F5A623]/10 border border-[#E85A1C]/20 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <span className="text-coral font-mono text-xs mb-2 block">COMPANY BROCHURE</span>
                <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-2">Avi Cement Limited Brochure</h3>
                <p className="text-gray-400 text-sm">Hamari complete product range, technical specs, aur company ki puri kahani — ek PDF mein.</p>
              </div>
              <a
                href={media.brochure_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="brochure-download-link"
                className="flex items-center gap-2 bg-[#E85A1C] hover:bg-[#d14e15] text-white font-display font-medium px-8 py-4 rounded-full transition-all whitespace-nowrap"
              >
                <FileDown className="w-5 h-5" /> Download Brochure
              </a>
            </div>
          </div>
        </section>
      )}

      {/* YouTube CTA */}
      <section className="py-16 bg-surface border-t border-white/5">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-2">Hamara channel subscribe karein</h3>
              <p className="text-gray-400">Latest videos, factory tours, aur brand content se updated rahein.</p>
            </div>
            <a
              href="https://www.youtube.com/@avicementlimited5603"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="youtube-subscribe-link"
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-display font-medium px-6 py-3 rounded-full transition-all"
            >
              <Play className="w-5 h-5 fill-white" /> YouTube Channel <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MediaPage;
